import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import GraphicElements from "../../../styles/svg/graphic_elements.svg";
import mobileBg from "../../../styles/svg/login_bg_mobile.svg";
import { useResponsive } from "../../../hooks/useResponsive";
import { useAssets } from "../../../utils/reactQuery/domainAssets";
import { getAPIBaseUrl } from "../../../utils/constants";
import TenantSiteName from "../../../components/TenantSiteName";
import { changePassword } from "../../../sevices/apiAuth";
import ForgotPasswordMessage from "../../../utils/Enums";
import { CustomButton } from "../../../components/Button/Button";
import { RFHTextField } from "../../../components/RFH";
import { createPasswordSchema, passwordRegex } from "../../../schemas/changePasswordSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { PassWordRequirement } from "../../../components/ChangePassword";

function ChangePassword({ authToken, onSuccess }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loginWithEmail, setLoginWith] = React.useState(false);
  const [apiError, setApiError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const { register, handleSubmit, reset, watch, ...methods } = useForm({
    defaultValues: {},
    resolver: yupResolver(createPasswordSchema),
  });
  const isMobile = useResponsive("down", "md");
  const { logo } = useAssets();
  const apiUrl = getAPIBaseUrl();
  const password = watch("new_password") || "";

  const submit = (data) => {
    setApiError("");
    setLoading(true);
    changePassword(data, authToken)
      .then((res) => {
        onSuccess();
      })
      .catch((err) => {
        console.error(err);
        if (err.code == "ERR_NETWORK") {
          setApiError("An unexpected error occurred. Please try again later");
        } else setApiError(err.response.data.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const passWordRegexList = Object.values(passwordRegex);
  const allRequirementsMet = passWordRegexList.map(regex => regex.test(password)).every(validation => validation);

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${isMobile ? mobileBg : GraphicElements})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "auto",
        minHeight: "100vh",
      }}
      disableGutters={true}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 440,
          justifyContent: "center",
          padding: "40px 15px",
        }}
      >
        {logo ? (
          <img
            src={`${apiUrl}/${logo}`}
            alt="not found"
            width={isMobile ? 251 : ""}
            className="login_logo"
          />
        ) : (
          <TenantSiteName />
        )}
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(submit)}
            noValidate
            className="change-password-form"
          >
            <Typography
              className="text-center black-primary-color create-new-password-title"
              component={"h1"}
              variant="h5"
            >
              Create New Password
            </Typography>
            <p className="create-new-password-info">
              Secure your account by setting a strong password.
            </p>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            <PassWordRequirement password={password} className="reset-password-requirement-list" />
            <RFHTextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="New Password"
              type="password"
              id="new_password"
              defaultPadding={false}
            />

            <RFHTextField
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              label={"Confirm Password"}
              name="confirmPassword"
              type="password"
              autoComplete="Confirm Password"
              className="primary-input-field"
              defaultPadding={false}
            />

          <CustomButton
            type="submit"
            fullWidth
            variant="contained"
            className="btn-blue"
            sx={{ mt: "14px", padding: "13px 16px" }}
            disabled={loading || !allRequirementsMet}
          >
            Save
          </CustomButton>
        </Box>
        </FormProvider>
      </Box>
    </Box>
  );
}

export default ChangePassword;
